import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar, Badge, Box, CircularProgress, Divider, IconButton, InputBase, Tooltip, Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import EditSquareIcon from '@mui/icons-material/BorderColor';
import SearchIcon from '@mui/icons-material/Search';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { CT, avatarTone, initialsOf, shortTime } from './chatTheme';
import BorrowerSupportPanel from './BorrowerSupportPanel';
import { listBorrowerUnread, sendHeartbeat } from './borrowerSupport.service';
import TypingIndicator from './TypingIndicator';
import { useTyping, typingLabel } from './useTyping';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem,
} from '@mui/material';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import GroupIcon from '@mui/icons-material/Group';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { Popper, Paper, ClickAwayListener } from '@mui/material';
import MessageAttachment from './MessageAttachment';
import EmojiPickerPopover from './EmojiPickerPopover';
import { isChatMuted, playMessageChime, setChatMuted } from './chatSound';
import {
  ChatConversation, ChatMessage, ChatPerson, ChatReaction, MESSAGE_PAGE_SIZE, QUICK_REACTIONS,
  addMembers, conversationTitle, deleteConversation, deleteMessage, editMessage, listConversations,
  listMessages, listParticipants, listReactions, markRead, newMessageId, otherReadTimes,
  removeMember, renameGroup, resolveMentions, searchPeople, senderName, sendMessage, splitMentions,
  startDirect, startGroup, toggleReaction, uploadAttachment,
} from './chat.service';
import { compressImage, imageFromClipboard } from './imageCompress';

type View = 'list' | 'thread' | 'new';

// Restores the page title after an unread badge is cleared.
const BASE_TITLE = typeof document !== 'undefined' ? document.title : '';

/**
 * Floating team chat. Available on every authenticated screen in both apps —
 * staff (samofundmobile) and agents (samofundagency) share one directory
 * because both run against the same Supabase project.
 */
const ChatWidget: React.FC = () => {
  const { userProfile } = useAuth();
  const selfId = userProfile?.id;
  const selfName = userProfile?.username || 'Someone';

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('list');
  // Which inbox the list is showing: internal team, or borrower support.
  const [tab, setTab] = useState<'team' | 'borrowers'>('team');
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [people, setPeople] = useState<ChatPerson[]>([]);
  const [peopleTerm, setPeopleTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Group-composer state (the "New message" screen doubles as the group builder).
  const [groupMode, setGroupMode] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState<ChatPerson[]>([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  // History paging
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Per-message action menu + inline edit
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuMessage, setMenuMessage] = useState<ChatMessage | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  // Read receipts: latest time every other participant has read up to.
  const [othersReadAt, setOthersReadAt] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reactions, setReactions] = useState<ChatReaction[]>([]);
  // Where the thread should land when it opens: the first unread message, or
  // the bottom. Also gates the "load older" scroll trigger so the initial
  // scroll-to-bottom isn't mistaken for the user reaching the top.
  const [unreadMarkerId, setUnreadMarkerId] = useState<string | null>(null);
  const initialScrollDoneRef = useRef(false);
  const unreadAnchorRef = useRef<HTMLDivElement | null>(null);
  // Composer emoji picker
  const [emojiAnchor, setEmojiAnchor] = useState<null | HTMLElement>(null);
  // Conversation settings (rename / add people / leave / delete)
  const [convMenuAnchor, setConvMenuAnchor] = useState<null | HTMLElement>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addingPeople, setAddingPeople] = useState(false);
  // Group members list
  const [membersOpen, setMembersOpen] = useState(false);
  const [members, setMembers] = useState<ChatPerson[]>([]);
  // @mention autocomplete
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  // Sound preference (persisted)
  const [muted, setMuted] = useState(isChatMuted());

  const scrollRef = useRef<HTMLDivElement>(null);
  const activeConv = useMemo(
    () => conversations.find((c) => c.id === activeId) || null,
    [conversations, activeId],
  );
  const { typingPeers, notifyTyping, notifyStopped } = useTyping(
    view === 'thread' ? activeId : null, selfId, selfName,
  );

  // Borrower unread lives here, not in BorrowerSupportPanel: the panel only
  // mounts once the widget is open and the Borrowers tab is picked, so a badge
  // owned by it would stay at zero exactly when it matters most.
  const [borrowerUnread, setBorrowerUnread] = useState<Record<string, number>>({});
  const refreshBorrowerUnread = useCallback(async () => {
    try {
      setBorrowerUnread(await listBorrowerUnread());
    } catch {
      /* the migration may not be applied yet - never break team chat over it */
    }
  }, []);

  const borrowerUnreadTotal = useMemo(
    () => Object.values(borrowerUnread).reduce((sum, n) => sum + n, 0),
    [borrowerUnread],
  );

  // Borrowers waiting on a reply count toward the same badge as colleagues.
  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + c.unread, 0) + borrowerUnreadTotal,
    [conversations, borrowerUnreadTotal],
  );

  const refreshList = useCallback(async () => {
    if (!selfId) return;
    setLoadingList(true);
    try {
      setConversations(await listConversations(selfId));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Unable to load conversations.');
    } finally {
      setLoadingList(false);
    }
  }, [selfId]);

  // Load once on sign-in so the badge is accurate before the panel is opened.
  useEffect(() => { if (selfId) void refreshList(); }, [selfId, refreshList]);
  useEffect(() => { if (selfId) void refreshBorrowerUnread(); }, [selfId, refreshBorrowerUnread]);

  // Refs let the single realtime subscription read current UI state without
  // being torn down and rebuilt on every render.
  const activeIdRef = useRef<string | null>(null);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);
  const conversationsRef = useRef<ChatConversation[]>([]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // Ask for permission on first open rather than ambushing the user on load.
  useEffect(() => {
    if (!open || typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') void Notification.requestPermission();
  }, [open]);

  const notify = useCallback((title: string, body: string) => {
    try {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
      const n = new Notification(title, { body: body.slice(0, 140), icon: '/samofund-logo.png' });
      n.onclick = () => { window.focus(); setOpen(true); n.close(); };
    } catch {
      /* notifications are a nicety - never break the chat over them */
    }
  }, []);

  // Presence heartbeat. The borrower app reads bapp_staff_presence to show who
  // is available; without this nobody is ever "online" and support routing
  // silently falls back to the branch manager.
  useEffect(() => {
    if (!selfId) return undefined;
    void sendHeartbeat();
    const id = window.setInterval(() => { void sendHeartbeat(); }, 60_000);
    return () => window.clearInterval(id);
  }, [selfId]);

  // Unread count in the tab title, so it's visible from another tab.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = totalUnread > 0 ? `(${totalUnread}) ${BASE_TITLE}` : BASE_TITLE;
    return () => { document.title = BASE_TITLE; };
  }, [totalUnread]);

  /** Earliest time every other participant has read up to (drives "Seen"). */
  const refreshReceipts = useCallback(async (conversationId: string) => {
    if (!selfId) return;
    const times = await otherReadTimes(conversationId, selfId);
    setOthersReadAt(times.length === 0 ? null : times.sort()[0]);
  }, [selfId]);

  // Live messages. One subscription for the whole widget: it appends to the
  // open thread, refreshes list previews/badges, and raises a desktop
  // notification when the message arrives while chat isn't being watched.
  useEffect(() => {
    if (!selfId) return;
    const channel = supabase
      .channel('chat-messages-stream')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, (payload) => {
        const msg = payload.new as ChatMessage;
        setMessages((prev) => {
          if (msg.conversation_id !== activeIdRef.current) return prev;
          if (prev.some((m) => m.id === msg.id)) return prev; // ignore own echo
          return [...prev, msg];
        });

        if (msg.sender_id !== selfId) {
          const mentionsMe = Boolean(selfId && msg.mentions?.includes(selfId));
          // Chime on every incoming message, with a brighter ring when you're
          // @mentioned. Plays even with the thread open so you notice a message
          // arriving while looking elsewhere on the page.
          playMessageChime(mentionsMe);

          // Desktop notification only when you're not already reading it.
          const alreadyReading = openRef.current && msg.conversation_id === activeIdRef.current;
          if (!alreadyReading) {
            const conv = conversationsRef.current.find((c) => c.id === msg.conversation_id);
            const who = senderName(conv, msg.sender_id);
            notify(mentionsMe ? `${who} mentioned you` : who, msg.body);
          }
        }

        void refreshList();
      })
      // Edits / soft-deletes made in another tab or by the sender.
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_messages' }, (payload) => {
        const msg = payload.new as ChatMessage;
        if (msg.conversation_id !== activeIdRef.current) return;
        setMessages((prev) => (
          msg.deleted_at
            ? prev.filter((m) => m.id !== msg.id)
            : prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m))
        ));
      })
      // Reactions from anyone in the open thread.
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_message_reactions' }, () => {
        const ids = messagesRef.current.map((m) => m.id);
        if (ids.length > 0) void listReactions(ids).then(setReactions);
      })
      // A borrower wrote in. RLS limits the stream to conversations this agent
      // owns, so no filter is needed and no other agent's threads leak through.
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'bapp_messages' }, (payload) => {
        const row = payload.new as { sender?: string; body?: string };
        if (row?.sender !== 'borrower') return;
        void refreshBorrowerUnread();
        playMessageChime(false);
        if (!openRef.current) notify('New borrower message', row.body ?? '');
      })
      // Someone read the thread -> refresh the "Seen" marker.
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_participants' }, (payload) => {
        const row = payload.new as any;
        if (row?.conversation_id && row.conversation_id === activeIdRef.current
            && row.profile_id !== selfId) {
          void refreshReceipts(row.conversation_id);
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [selfId, refreshList, notify, refreshReceipts, refreshBorrowerUnread]);

  // Keep the newest message in view — but only when the user is already near
  // the bottom, so reading history isn't yanked away by an incoming message.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !initialScrollDoneRef.current) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages, typingPeers.length]);

  // On open: jump to the first unread message, else to the latest. Runs after
  // paint so the list has real heights; images settle via the load handler below.
  useEffect(() => {
    if (view !== 'thread' || loadingThread || messages.length === 0) return;
    if (initialScrollDoneRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      if (unreadMarkerId && unreadAnchorRef.current) {
        // Show a little context above the divider rather than pinning it flush.
        unreadAnchorRef.current.scrollIntoView({ block: 'center' });
      } else {
        el.scrollTop = el.scrollHeight;
      }
      initialScrollDoneRef.current = true;
    });
  }, [view, loadingThread, messages, unreadMarkerId]);

  const openConversation = useCallback(async (conversationId: string) => {
    setActiveId(conversationId);
    setView('thread');
    setLoadingThread(true);
    setEditingId(null);
    setReactions([]);
    setUnreadMarkerId(null);
    initialScrollDoneRef.current = false; // re-arm the open-position logic
    try {
      // Capture my read position BEFORE marking the thread read, so the
      // "New messages" divider lands where the user actually left off.
      const readAt = conversations.find((c) => c.id === conversationId)?.last_read_at;

      const page = await listMessages(conversationId);
      setMessages(page);
      setHasMore(page.length >= MESSAGE_PAGE_SIZE);

      if (readAt) {
        const firstUnread = page.find(
          (m) => m.sender_id !== selfId && new Date(m.created_at) > new Date(readAt));
        setUnreadMarkerId(firstUnread?.id ?? null);
      }

      void listReactions(page.map((m) => m.id)).then(setReactions);

      if (selfId) {
        await markRead(conversationId, selfId);
        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? { ...c, unread: 0 } : c)));
      }
      void refreshReceipts(conversationId);
    } catch (e: any) {
      setError(e?.message || 'Unable to open that conversation.');
    } finally {
      setLoadingThread(false);
    }
  }, [selfId, refreshReceipts, conversations]);

  /** Infinite scroll: fetch the previous page when the user reaches the top,
   *  preserving scroll position so the view doesn't jump. */
  const loadOlder = useCallback(async () => {
    if (!activeId || loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const el = scrollRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    try {
      const older = await listMessages(activeId, { before: messages[0].created_at });
      if (older.length === 0) { setHasMore(false); return; }
      setMessages((prev) => [...older, ...prev]);
      setHasMore(older.length >= MESSAGE_PAGE_SIZE);
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevHeight;
      });
    } catch (e: any) {
      setError(e?.message || 'Unable to load older messages.');
    } finally {
      setLoadingMore(false);
    }
  }, [activeId, loadingMore, hasMore, messages]);

  const handleSend = useCallback(async () => {
    const text = draft.trim();
    if (!text || !activeId || !selfId || sending) return;
    setSending(true);
    // The id is generated HERE and reused for the insert, so the realtime echo
    // carries the same id and dedups against this optimistic row. (Using a
    // temp id made every sent message appear twice until a refresh.)
    const id = newMessageId();
    // Store WHO was mentioned as ids, so a later rename can't break it.
    const mentions = resolveMentions(text, activeConv?.others || []);
    const optimistic: ChatMessage = {
      id,
      conversation_id: activeId,
      sender_id: selfId,
      body: text,
      created_at: new Date().toISOString(),
      deleted_at: null,
      mentions,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    setMentionQuery(null);
    notifyStopped();
    try {
      await sendMessage(activeId, selfId, text, id, null, mentions);
      void refreshList();
    } catch (e: any) {
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setDraft(text); // hand the text back rather than losing it
      setError(e?.message || 'Message not sent.');
    } finally {
      setSending(false);
    }
  }, [draft, activeId, selfId, sending, notifyStopped, refreshList, activeConv]);

  const openMembers = useCallback(async () => {
    if (!activeId) return;
    setMembersOpen(true);
    try {
      setMembers(await listParticipants(activeId));
    } catch (e: any) {
      setError(e?.message || 'Unable to load members.');
    }
  }, [activeId]);

  const handleAttach = useCallback(async (file: File) => {
    if (!activeId || !selfId) return;
    setUploading(true);
    try {
      // Shrink images before upload; text stays legible (see imageCompress).
      const prepared = await compressImage(file);
      const uploaded = await uploadAttachment(activeId, prepared);
      await sendMessage(activeId, selfId, draft.trim(), newMessageId(), uploaded);
      setDraft('');
      void refreshList();
    } catch (e: any) {
      setError(e?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [activeId, selfId, draft, refreshList]);

  /** Paste a screenshot straight into the thread. */
  const handlePaste = useCallback((event: React.ClipboardEvent) => {
    const image = imageFromClipboard(event);
    if (!image) return; // plain text paste - leave it alone
    event.preventDefault();
    void handleAttach(image);
  }, [handleAttach]);

  /** Insert an emoji at the end of the draft and keep typing. */
  const insertEmoji = useCallback((emoji: string) => {
    setDraft((prev) => prev + emoji);
    notifyTyping();
  }, [notifyTyping]);

  // ── @mentions ────────────────────────────────────────────────────────────
  /** Track a trailing "@fragment" so the picker can follow what's being typed. */
  const updateMentionQuery = useCallback((value: string) => {
    // Only the token being typed at the very end counts, and only in groups.
    const match = /(?:^|\s)@([\w.\- ]{0,24})$/.exec(value);
    setMentionQuery(activeConv?.is_group && match ? match[1] : null);
  }, [activeConv]);

  const mentionMatches = useMemo(() => {
    if (mentionQuery === null || !activeConv?.is_group) return [];
    const term = mentionQuery.trim().toLowerCase();
    return activeConv.others
      .filter((p) => !term || p.username.toLowerCase().includes(term))
      .slice(0, 6);
  }, [mentionQuery, activeConv]);

  /** Replace the in-progress "@fragment" with the chosen person's name. */
  const applyMention = useCallback((person: ChatPerson) => {
    setDraft((prev) => prev.replace(/(^|\s)@([\w.\- ]{0,24})$/, `$1@${person.username} `));
    setMentionQuery(null);
  }, []);

  // ── Conversation settings ────────────────────────────────────────────────
  const isOwner = Boolean(activeConv && selfId && activeConv.created_by === selfId);

  const commitRename = useCallback(async () => {
    if (!activeId) return;
    const title = renameDraft.trim();
    setRenaming(false);
    try {
      await renameGroup(activeId, title);
      setConversations((prev) => prev.map((c) => (
        c.id === activeId ? { ...c, title: title || null } : c)));
    } catch (e: any) {
      setError(e?.message || 'Unable to rename this group.');
    }
  }, [activeId, renameDraft]);

  const handleAddMembers = useCallback(async (people: ChatPerson[]) => {
    if (!activeId || people.length === 0) return;
    try {
      await addMembers(activeId, people.map((p) => p.id));
      await refreshList();
      setAddingPeople(false);
      setGroupMembers([]);
    } catch (e: any) {
      setError(e?.message || 'Unable to add people.');
    }
  }, [activeId, refreshList]);

  const handleLeave = useCallback(async () => {
    if (!activeId || !selfId) return;
    try {
      await removeMember(activeId, selfId);
      setActiveId(null);
      setView('list');
      await refreshList();
    } catch (e: any) {
      setError(e?.message || 'Unable to leave this conversation.');
    }
  }, [activeId, selfId, refreshList]);

  const handleDeleteConversation = useCallback(async () => {
    if (!activeId) return;
    setConfirmDelete(false);
    try {
      await deleteConversation(activeId);
      setActiveId(null);
      setView('list');
      await refreshList();
    } catch (e: any) {
      setError(e?.message || 'Unable to delete this conversation.');
    }
  }, [activeId, refreshList]);

  const handleReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!selfId) return;
    const mineAlready = reactions.some(
      (r) => r.message_id === messageId && r.profile_id === selfId && r.emoji === emoji);
    // Optimistic — reactions should feel instant.
    setReactions((prev) => (mineAlready
      ? prev.filter((r) => !(r.message_id === messageId && r.profile_id === selfId && r.emoji === emoji))
      : [...prev, { message_id: messageId, profile_id: selfId, emoji }]));
    try {
      await toggleReaction(messageId, selfId, emoji, !mineAlready);
    } catch (e: any) {
      setError(e?.message || 'Reaction failed.');
      void listReactions(messages.map((m) => m.id)).then(setReactions);
    }
  }, [selfId, reactions, messages]);

  const commitEdit = useCallback(async () => {
    if (!editingId) return;
    const text = editDraft.trim();
    const original = messages.find((m) => m.id === editingId);
    if (!text || text === original?.body) { setEditingId(null); return; }
    setMessages((prev) => prev.map((m) => (
      m.id === editingId ? { ...m, body: text, edited_at: new Date().toISOString() } : m
    )));
    setEditingId(null);
    try {
      await editMessage(editingId, text);
      void refreshList();
    } catch (e: any) {
      setError(e?.message || 'Unable to edit that message.');
      if (original) setMessages((prev) => prev.map((m) => (m.id === original.id ? original : m)));
    }
  }, [editingId, editDraft, messages, refreshList]);

  const removeMessage = useCallback(async (message: ChatMessage) => {
    setMessages((prev) => prev.filter((m) => m.id !== message.id));
    try {
      await deleteMessage(message.id);
      void refreshList();
    } catch (e: any) {
      setError(e?.message || 'Unable to delete that message.');
      setMessages((prev) => [...prev, message].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()));
    }
  }, [refreshList]);

  const loadPeople = useCallback(async (term: string) => {
    if (!selfId) return;
    try {
      setPeople(await searchPeople(term, selfId));
    } catch (e: any) {
      setError(e?.message || 'Unable to load people.');
    }
  }, [selfId]);

  const beginNew = useCallback(() => {
    setView('new');
    setPeopleTerm('');
    setGroupMode(false);
    setGroupName('');
    setGroupMembers([]);
    void loadPeople('');
  }, [loadPeople]);

  const toggleMember = useCallback((person: ChatPerson) => {
    setGroupMembers((prev) => (
      prev.some((p) => p.id === person.id)
        ? prev.filter((p) => p.id !== person.id)
        : [...prev, person]
    ));
  }, []);

  const createGroup = useCallback(async () => {
    if (groupMembers.length < 2 || creatingGroup) return;
    setCreatingGroup(true);
    try {
      const conversationId = await startGroup(groupName, groupMembers.map((p) => p.id));
      await refreshList();
      await openConversation(conversationId);
    } catch (e: any) {
      setError(e?.message || 'Unable to create the group.');
    } finally {
      setCreatingGroup(false);
    }
  }, [groupMembers, groupName, creatingGroup, refreshList, openConversation]);

  const startWith = useCallback(async (person: ChatPerson) => {
    try {
      const conversationId = await startDirect(person.id);
      await refreshList();
      await openConversation(conversationId);
    } catch (e: any) {
      setError(e?.message || 'Unable to start that conversation.');
    }
  }, [refreshList, openConversation]);

  if (!selfId) return null; // signed out — nothing to show

  // ── Shared bits ───────────────────────────────────────────────────────────
  /** Resolve a participant id to a display name (for reaction tooltips). */
  const personName = (profileId: string): string => {
    if (profileId === selfId) return 'You';
    const inThread = activeConv?.others.find((p) => p.id === profileId);
    if (inThread) return inThread.username;
    // Could be someone who has since left the conversation.
    for (const conv of conversations) {
      const match = conv.others.find((p) => p.id === profileId);
      if (match) return match.username;
    }
    return 'Someone';
  };

  const personAvatar = (person: { username: string; avatar_url?: string | null }, size = 36) => {
    const tone = avatarTone(person.username || '?');
    return (
      <Avatar
        src={person.avatar_url || undefined}
        sx={{ width: size, height: size, bgcolor: tone.bg, color: tone.fg, fontSize: size * 0.38, fontWeight: 700 }}
      >
        {initialsOf(person.username)}
      </Avatar>
    );
  };

  const headerTitle =
    view === 'new' ? 'New message'
      : view === 'thread' && activeConv ? conversationTitle(activeConv)
        : 'Messages';

  return (
    <>
      {/* ── Launcher ───────────────────────────────────────────────────── */}
      {!open && (
        <Tooltip title="Messages" placement="left">
          <Badge
            badgeContent={totalUnread}
            max={99}
            sx={{
              position: 'fixed', right: 24, bottom: 24, zIndex: CT.z,
              '& .MuiBadge-badge': { bgcolor: CT.pink, color: '#fff', fontWeight: 700, fontSize: 11 },
            }}
          >
            <IconButton
              aria-label={totalUnread ? `Messages, ${totalUnread} unread` : 'Messages'}
              onClick={() => setOpen(true)}
              sx={{
                width: 56, height: 56, bgcolor: CT.green, color: '#fff',
                boxShadow: '0 6px 20px rgba(15,78,39,.28)', transition: CT.motion,
                '&:hover': { bgcolor: CT.greenHover, transform: 'translateY(-1px)' },
              }}
            >
              <ChatBubbleOutlineIcon />
            </IconButton>
          </Badge>
        </Tooltip>
      )}

      {/* ── Panel ──────────────────────────────────────────────────────── */}
      {open && (
        <Box
          role="dialog"
          aria-label="Team messages"
          sx={{
            position: 'fixed', zIndex: CT.z, bgcolor: CT.white,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            // Phone: full-height sheet. Desktop: anchored card.
            inset: { xs: 0, sm: 'auto' },
            right: { sm: 24 }, bottom: { sm: 24 },
            width: { xs: '100%', sm: 380 },
            height: { xs: '100%', sm: 560 },
            maxHeight: { sm: 'calc(100vh - 48px)' },
            borderRadius: { xs: 0, sm: `${CT.radius.xl}px` },
            border: { sm: `1px solid ${CT.border}` },
            boxShadow: '0 12px 40px rgba(16,24,40,.18)',
            '@keyframes sfChatIn': {
              from: { opacity: 0, transform: 'translateY(10px) scale(.99)' },
              to: { opacity: 1, transform: 'none' },
            },
            animation: 'sfChatIn .16s ease-out',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.25, bgcolor: CT.forest, color: '#fff' }}>
            {view !== 'list' ? (
              <IconButton size="small" onClick={() => setView('list')} sx={{ color: '#fff' }} aria-label="Back">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 8 }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15 }} noWrap>{headerTitle}</Typography>
              {view === 'thread' && activeConv?.is_group && (
                // Tapping the member count opens the full list.
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={() => void openMembers()}
                  onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') void openMembers(); }}
                  sx={{
                    fontSize: 11, color: 'rgba(255,255,255,.75)', cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {activeConv.others.length + 1} members
                </Typography>
              )}
            </Box>
            <Tooltip title={muted ? 'Sound off — click to unmute' : 'Sound on — click to mute'}>
              <IconButton
                size="small"
                onClick={() => { const next = !muted; setMuted(next); setChatMuted(next); }}
                sx={{ color: '#fff' }}
                aria-label={muted ? 'Unmute chat sound' : 'Mute chat sound'}
              >
                {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {view === 'list' && (
              <Tooltip title="New message">
                <IconButton size="small" onClick={beginNew} sx={{ color: '#fff' }} aria-label="New message">
                  <EditSquareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {view === 'thread' && activeConv && (
              <Tooltip title="Conversation settings">
                <IconButton
                  size="small"
                  onClick={(e) => setConvMenuAnchor(e.currentTarget)}
                  sx={{ color: '#fff' }}
                  aria-label="Conversation settings"
                >
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff' }} aria-label="Close messages">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {error && (
            <Box sx={{ px: 2, py: 1, bgcolor: CT.pinkTint, color: '#9A2367', fontSize: 12.5 }}>{error}</Box>
          )}

          {/* Team vs borrower inbox. Borrower threads live in bapp_messages,
              never in the team-chat tables — see borrowerSupport.service.ts. */}
          {view === 'list' && (
            <Box sx={{ display: 'flex', gap: 0.5, px: 1.25, py: 0.75, bgcolor: CT.white, borderBottom: `1px solid ${CT.border}` }}>
              {(['team', 'borrowers'] as const).map((key) => {
                const on = tab === key;
                const tabUnread = key === 'borrowers'
                  ? borrowerUnreadTotal
                  : conversations.reduce((sum, c) => sum + c.unread, 0);
                return (
                  <Box
                    key={key}
                    role="button"
                    tabIndex={0}
                    onClick={() => setTab(key)}
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') setTab(key); }}
                    sx={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                      cursor: 'pointer', userSelect: 'none',
                      py: 0.75, borderRadius: `${CT.radius.md}px`, transition: CT.motion,
                      bgcolor: on ? CT.greenTint : 'transparent',
                      color: on ? CT.forest : CT.muted,
                      fontWeight: 700, fontSize: 12.5,
                      '&:hover': { bgcolor: on ? CT.greenTint : CT.bone },
                    }}
                  >
                    {key === 'team' ? 'Team' : 'Borrowers'}
                    {tabUnread > 0 && (
                      <Box component="span" sx={{
                        minWidth: 17, height: 17, px: 0.5, display: 'grid', placeItems: 'center',
                        borderRadius: 999, bgcolor: CT.pink, color: '#fff', fontSize: 10, fontWeight: 700,
                      }}>
                        {tabUnread > 99 ? '99+' : tabUnread}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          {view === 'list' && tab === 'borrowers' && (
            <BorrowerSupportPanel unread={borrowerUnread} onUnreadChange={() => { void refreshBorrowerUnread(); }} />
          )}

          {/* ── Conversation list ─────────────────────────────────────── */}
          {view === 'list' && tab === 'team' && (
            <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: CT.white }}>
              {loadingList && conversations.length === 0 ? (
                <Box sx={{ display: 'grid', placeItems: 'center', height: 160 }}><CircularProgress size={22} /></Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 34, color: CT.faint }} />
                  <Typography sx={{ mt: 1.5, fontWeight: 700, color: CT.ink }}>No messages yet</Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: CT.muted }}>
                    Start a conversation with anyone on the team.
                  </Typography>
                  <Box
                    component="button"
                    onClick={beginNew}
                    sx={{
                      mt: 2, px: 2, py: 1, border: 'none', cursor: 'pointer',
                      borderRadius: `${CT.radius.md}px`, bgcolor: CT.green, color: '#fff',
                      fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                      '&:hover': { bgcolor: CT.greenHover },
                    }}
                  >
                    New message
                  </Box>
                </Box>
              ) : (
                conversations.map((c) => {
                  const other = c.others[0];
                  const title = conversationTitle(c);
                  const mine = c.last_message_sender === selfId;
                  return (
                    <Box
                      key={c.id}
                      onClick={() => void openConversation(c.id)}
                      sx={{
                        display: 'flex', gap: 1.25, alignItems: 'center', px: 1.75, py: 1.25,
                        cursor: 'pointer', transition: CT.motion,
                        borderBottom: `1px solid ${CT.hairline}`,
                        bgcolor: c.unread > 0 ? CT.greenTint : 'transparent',
                        '&:hover': { bgcolor: c.unread > 0 ? CT.greenTint : CT.bone },
                      }}
                    >
                      {personAvatar(other || { username: title })}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                          <Typography sx={{ flex: 1, fontWeight: c.unread ? 800 : 600, fontSize: 13.5, color: CT.ink }} noWrap>
                            {title}
                          </Typography>
                          <Typography sx={{ fontSize: 10.5, color: CT.faint, fontFamily: CT.mono, flexShrink: 0 }}>
                            {shortTime(c.last_message_at)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12.5, color: c.unread ? CT.inkSoft : CT.muted, fontWeight: c.unread ? 600 : 400 }} noWrap>
                          {c.last_message_preview ? `${mine ? 'You: ' : ''}${c.last_message_preview}` : 'No messages yet'}
                        </Typography>
                      </Box>
                      {c.unread > 0 && (
                        <Box sx={{
                          minWidth: 20, height: 20, px: 0.75, borderRadius: '999px', bgcolor: CT.pink,
                          color: '#fff', fontSize: 11, fontWeight: 800, display: 'grid', placeItems: 'center',
                        }}>
                          {c.unread > 99 ? '99+' : c.unread}
                        </Box>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          )}

          {/* ── New message: 1:1 picker, or group builder ──────────────── */}
          {view === 'new' && (
            <>
              {/* Mode switch */}
              <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, m: 1.5, mb: 0, bgcolor: CT.bone, borderRadius: `${CT.radius.md}px` }}>
                {([['Direct', false], ['Group', true]] as const).map(([label, isGroup]) => (
                  <Box
                    key={label}
                    role="button"
                    tabIndex={0}
                    onClick={() => { setGroupMode(isGroup); setGroupMembers([]); }}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setGroupMode(isGroup); setGroupMembers([]); }
                    }}
                    sx={{
                      flex: 1, textAlign: 'center', py: 0.65, cursor: 'pointer', userSelect: 'none',
                      borderRadius: `${CT.radius.sm}px`, fontSize: 12.5, fontWeight: 700, transition: CT.motion,
                      bgcolor: groupMode === isGroup ? CT.white : 'transparent',
                      color: groupMode === isGroup ? CT.green : CT.muted,
                      boxShadow: groupMode === isGroup ? '0 1px 2px rgba(16,24,40,.10)' : 'none',
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>

              {groupMode && (
                <Box sx={{ px: 1.5, pt: 1.25 }}>
                  <InputBase
                    placeholder="Group name (optional)"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    sx={{
                      width: '100%', px: 1.25, py: 0.75, fontSize: 13.5, bgcolor: CT.bone,
                      borderRadius: `${CT.radius.md}px`, border: `1px solid ${CT.border}`,
                    }}
                  />
                  {groupMembers.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                      {groupMembers.map((person) => (
                        <Box
                          key={person.id}
                          onClick={() => toggleMember(person)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, pl: 1, pr: 0.75, py: 0.3,
                            borderRadius: '999px', bgcolor: CT.greenTint, color: CT.forest,
                            fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                          }}
                        >
                          {person.username}
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 1.5, px: 1.25, py: 0.75, bgcolor: CT.bone, borderRadius: `${CT.radius.md}px` }}>
                <SearchIcon sx={{ fontSize: 18, color: CT.muted }} />
                <InputBase
                  autoFocus
                  placeholder="Search people…"
                  value={peopleTerm}
                  onChange={(e) => { setPeopleTerm(e.target.value); void loadPeople(e.target.value); }}
                  sx={{ flex: 1, fontSize: 13.5 }}
                />
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {people.length === 0 ? (
                  <Typography sx={{ p: 3, textAlign: 'center', fontSize: 13, color: CT.muted }}>
                    No one matches that name.
                  </Typography>
                ) : people.map((person) => {
                  const picked = groupMembers.some((p) => p.id === person.id);
                  return (
                    <Box
                      key={person.id}
                      onClick={() => (groupMode ? toggleMember(person) : void startWith(person))}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.1, cursor: 'pointer',
                        borderBottom: `1px solid ${CT.hairline}`, transition: CT.motion,
                        bgcolor: picked ? CT.greenTint : 'transparent',
                        '&:hover': { bgcolor: picked ? CT.greenTint : CT.bone },
                      }}
                    >
                      {personAvatar(person, 32)}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: CT.ink }} noWrap>{person.username}</Typography>
                        {person.role && (
                          <Typography sx={{ fontSize: 10.5, color: CT.faint, fontFamily: CT.mono, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                            {person.role}
                          </Typography>
                        )}
                      </Box>
                      {groupMode && (
                        <Box sx={{
                          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                          border: `2px solid ${picked ? CT.green : CT.border}`,
                          bgcolor: picked ? CT.green : 'transparent',
                          display: 'grid', placeItems: 'center', color: '#fff', fontSize: 11, fontWeight: 900,
                        }}>
                          {picked ? '✓' : ''}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {groupMode && (
                <>
                  <Divider />
                  <Box sx={{ p: 1.25, bgcolor: CT.white }}>
                    <Box
                      component="button"
                      disabled={groupMembers.length < 2 || creatingGroup}
                      onClick={() => void createGroup()}
                      sx={{
                        width: '100%', py: 1.1, border: 'none', borderRadius: `${CT.radius.md}px`,
                        fontFamily: 'inherit', fontWeight: 700, fontSize: 13.5, transition: CT.motion,
                        cursor: groupMembers.length < 2 ? 'not-allowed' : 'pointer',
                        bgcolor: groupMembers.length < 2 ? CT.hairline : CT.green,
                        color: groupMembers.length < 2 ? CT.faint : '#fff',
                      }}
                    >
                      {creatingGroup
                        ? 'Creating…'
                        : groupMembers.length < 2
                          ? 'Pick at least 2 people'
                          : `Create group with ${groupMembers.length} people`}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}

          {/* ── Thread ────────────────────────────────────────────────── */}
          {view === 'thread' && (
            <>
              <Box
                ref={scrollRef}
                onScroll={(e) => {
                  // Ignore until the opening scroll has run, otherwise the
                  // initial scrollTop of 0 instantly pages in older messages.
                  if (!initialScrollDoneRef.current) return;
                  if ((e.target as HTMLElement).scrollTop < 40) void loadOlder();
                }}
                sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, bgcolor: CT.bg }}
              >
                {loadingMore && (
                  <Box sx={{ display: 'grid', placeItems: 'center', py: 1 }}><CircularProgress size={16} /></Box>
                )}
                {!hasMore && !loadingThread && messages.length > 0 && (
                  <Typography sx={{ textAlign: 'center', fontSize: 10.5, color: CT.faint, fontFamily: CT.mono, mb: 1 }}>
                    START OF CONVERSATION
                  </Typography>
                )}
                {loadingThread ? (
                  <Box sx={{ display: 'grid', placeItems: 'center', height: 140 }}><CircularProgress size={22} /></Box>
                ) : messages.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', fontSize: 13, color: CT.muted, mt: 4 }}>
                    Say hello 👋
                  </Typography>
                ) : messages.map((msg, index) => {
                  const mine = msg.sender_id === selfId;
                  const prev = messages[index - 1];
                  // Group consecutive messages from the same person.
                  const grouped = prev && prev.sender_id === msg.sender_id
                    && new Date(msg.created_at).getTime() - new Date(prev.created_at).getTime() < 5 * 60000;
                  const sender = activeConv?.others.find((p) => p.id === msg.sender_id);
                  const editing = editingId === msg.id;
                  // "Seen" belongs on the newest of my messages only.
                  const isLastMine = mine && !messages.slice(index + 1).some((m) => m.sender_id === selfId);
                  const seen = Boolean(isLastMine && othersReadAt
                    && new Date(othersReadAt) >= new Date(msg.created_at));
                  // Group this message's reactions by emoji, keeping the names so
                  // the sender can see WHO reacted (shown on hover).
                  const reactionCounts = reactions
                    .filter((r) => r.message_id === msg.id)
                    .reduce<Record<string, { count: number; mine: boolean; names: string[] }>>(
                      (acc, r) => {
                        const entry = acc[r.emoji] || { count: 0, mine: false, names: [] };
                        entry.count += 1;
                        if (r.profile_id === selfId) {
                          entry.mine = true;
                          entry.names.push('You');
                        } else {
                          entry.names.push(personName(r.profile_id));
                        }
                        acc[r.emoji] = entry;
                        return acc;
                      }, {});

                  return (
                    <React.Fragment key={msg.id}>
                    {unreadMarkerId === msg.id && (
                      <Box ref={unreadAnchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
                        <Box sx={{ flex: 1, height: 1, bgcolor: CT.pink, opacity: 0.35 }} />
                        <Typography sx={{
                          fontSize: 10, fontFamily: CT.mono, fontWeight: 700, color: CT.pink,
                          letterSpacing: '.08em', textTransform: 'uppercase',
                        }}>
                          New messages
                        </Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: CT.pink, opacity: 0.35 }} />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start',
                        alignItems: 'center', gap: 0.5, mt: grouped ? 0.4 : 1.25,
                        '&:hover .sf-msg-actions': { opacity: 1 },
                      }}
                    >
                      {/* Revealed on hover. Available on every message (anyone can
                          react); edit/delete inside are limited to your own. */}
                      {!editing && (
                        <IconButton
                          className="sf-msg-actions"
                          size="small"
                          aria-label="Message actions"
                          onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuMessage(msg); }}
                          sx={{
                            opacity: 0, transition: CT.motion, color: CT.faint, width: 26, height: 26,
                            order: mine ? 0 : 2, // stays on the outside of the bubble
                          }}
                        >
                          <MoreHorizIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                      <Box sx={{ maxWidth: '78%' }}>
                        {!mine && !grouped && activeConv?.is_group && sender && (
                          <Typography sx={{ fontSize: 10.5, color: CT.muted, fontWeight: 700, mb: 0.25, ml: 0.5 }}>
                            {sender.username}
                          </Typography>
                        )}
                        <Box
                          sx={{
                            px: 1.5, py: 1, borderRadius: mine
                              ? `${CT.radius.lg}px ${CT.radius.lg}px 4px ${CT.radius.lg}px`
                              : `${CT.radius.lg}px ${CT.radius.lg}px ${CT.radius.lg}px 4px`,
                            bgcolor: mine ? CT.green : CT.white,
                            color: mine ? '#fff' : CT.ink,
                            border: mine ? 'none' : `1px solid ${CT.border}`,
                            fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          }}
                        >
                          {editing ? (
                            // No commit-on-blur: closing the actions menu returns
                            // focus to its anchor, which fired blur immediately and
                            // cancelled the edit before it could be typed into.
                            <Box>
                              <InputBase
                                autoFocus
                                multiline
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void commitEdit(); }
                                  if (e.key === 'Escape') { e.preventDefault(); setEditingId(null); }
                                }}
                                sx={{ fontSize: 13.5, color: '#fff', width: '100%', '& textarea': { p: 0 } }}
                              />
                              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, alignItems: 'center' }}>
                                <Box
                                  component="button"
                                  onClick={() => void commitEdit()}
                                  sx={{
                                    border: 'none', bgcolor: 'rgba(255,255,255,.22)', color: '#fff',
                                    fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                                    px: 1.25, py: 0.35, borderRadius: '999px',
                                  }}
                                >
                                  Save
                                </Box>
                                <Box
                                  component="button"
                                  onClick={() => setEditingId(null)}
                                  sx={{
                                    border: 'none', bgcolor: 'transparent', color: 'rgba(255,255,255,.85)',
                                    fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', p: 0,
                                  }}
                                >
                                  Cancel
                                </Box>
                              </Box>
                            </Box>
                          ) : (
                            <>
                              {msg.attachment_path && (
                                <MessageAttachment
                                  path={msg.attachment_path}
                                  name={msg.attachment_name || 'Attachment'}
                                  type={msg.attachment_type || ''}
                                  size={msg.attachment_size}
                                  mine={mine}
                                  onLoaded={() => {
                                    // An image that finishes loading changes the
                                    // thread height; keep the newest in view.
                                    const el = scrollRef.current;
                                    if (!el || unreadMarkerId) return;
                                    if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
                                      el.scrollTop = el.scrollHeight;
                                    }
                                  }}
                                />
                              )}
                              {/* A file-only message stores its filename as body; don't repeat it. */}
                              {msg.body && msg.body !== msg.attachment_name && (
                                <Box sx={{ mt: msg.attachment_path ? 0.75 : 0 }}>
                                  {splitMentions(msg.body, activeConv?.others || []).map((part, i) => (
                                    part.mention ? (
                                      <Box
                                        key={i}
                                        component="span"
                                        sx={{
                                          fontWeight: 700,
                                          px: 0.4,
                                          borderRadius: '4px',
                                          // Tinted on light bubbles; on my own green
                                          // bubble a wash keeps it readable.
                                          bgcolor: mine ? 'rgba(255,255,255,.22)' : CT.greenTint,
                                          color: mine ? '#fff' : CT.forest,
                                        }}
                                      >
                                        {part.text}
                                      </Box>
                                    ) : (
                                      <React.Fragment key={i}>{part.text}</React.Fragment>
                                    )
                                  ))}
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                        {/* Reaction chips — tap to add/remove your own. */}
                        {Object.keys(reactionCounts).length > 0 && (
                          <Box sx={{
                            display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5,
                            justifyContent: mine ? 'flex-end' : 'flex-start',
                          }}>
                            {Object.entries(reactionCounts).map(([emoji, info]) => (
                              // Hovering names the people who reacted.
                              <Tooltip
                                key={emoji}
                                title={`${info.names.join(', ')} reacted ${emoji}`}
                                placement="top"
                                arrow
                              >
                                <Box
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`${info.names.join(', ')} reacted ${emoji}`}
                                  onClick={() => void handleReaction(msg.id, emoji)}
                                  onKeyDown={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault(); void handleReaction(msg.id, emoji);
                                    }
                                  }}
                                  sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.35,
                                    px: 0.75, py: 0.15, borderRadius: '999px', cursor: 'pointer',
                                    fontSize: 12, lineHeight: 1.6, userSelect: 'none',
                                    bgcolor: info.mine ? CT.greenTint : CT.white,
                                    border: `1px solid ${info.mine ? CT.green : CT.border}`,
                                    transition: CT.motion,
                                    '&:hover': { borderColor: CT.green },
                                  }}
                                >
                                  <span>{emoji}</span>
                                  <Box component="span" sx={{
                                    fontSize: 10.5, fontWeight: 700,
                                    color: info.mine ? CT.forest : CT.muted,
                                  }}>
                                    {info.count}
                                  </Box>
                                </Box>
                              </Tooltip>
                            ))}
                          </Box>
                        )}
                        <Typography sx={{
                          fontSize: 10, color: CT.faint, fontFamily: CT.mono, mt: 0.3,
                          textAlign: mine ? 'right' : 'left',
                        }}>
                          {shortTime(msg.created_at)}
                          {msg.edited_at ? ' · edited' : ''}
                          {seen ? ' · Seen' : ''}
                        </Typography>
                      </Box>
                    </Box>
                    </React.Fragment>
                  );
                })}
              </Box>

              {/* Typing indicator sits between thread and composer */}
              {typingPeers.length > 0 && (
                <Box sx={{ bgcolor: CT.bg }}>
                  <TypingIndicator label={typingLabel(typingPeers)} />
                </Box>
              )}

              <Divider />
              {/* @mention picker, anchored above the composer */}
              <Popper
                open={mentionMatches.length > 0}
                anchorEl={composerRef.current}
                placement="top-start"
                style={{ zIndex: CT.z + 1 }}
              >
                <ClickAwayListener onClickAway={() => setMentionQuery(null)}>
                  <Paper sx={{ width: 240, maxHeight: 220, overflowY: 'auto', borderRadius: `${CT.radius.md}px`, mb: 0.5 }}>
                    {mentionMatches.map((person) => (
                      <Box
                        key={person.id}
                        onMouseDown={(e) => { e.preventDefault(); applyMention(person); }}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.9,
                          cursor: 'pointer', '&:hover': { bgcolor: CT.greenTint },
                        }}
                      >
                        {personAvatar(person, 26)}
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: CT.ink }} noWrap>
                          {person.username}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </ClickAwayListener>
              </Popper>
              <Box ref={composerRef} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1.25, bgcolor: CT.white }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleAttach(file);
                  }}
                />
                <Tooltip title="Attach a file (max 10 MB)">
                  <span>
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      aria-label="Attach a file"
                      sx={{ width: 36, height: 36, color: CT.muted, '&:hover': { color: CT.green, bgcolor: CT.greenTint } }}
                    >
                      {uploading ? <CircularProgress size={16} /> : <AttachFileIcon sx={{ fontSize: 19 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Emoji">
                  <IconButton
                    onClick={(e) => setEmojiAnchor(e.currentTarget)}
                    aria-label="Insert emoji"
                    sx={{ width: 36, height: 36, color: CT.muted, '&:hover': { color: CT.green, bgcolor: CT.greenTint } }}
                  >
                    <SentimentSatisfiedAltIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Tooltip>
                <InputBase
                  multiline
                  maxRows={4}
                  placeholder={activeConv?.is_group
                    ? 'Write a message, @mention, or paste a screenshot…'
                    : 'Write a message, or paste a screenshot…'}
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    updateMentionQuery(e.target.value);
                    notifyTyping();
                  }}
                  onPaste={handlePaste}
                  onBlur={notifyStopped}
                  onKeyDown={(e) => {
                    // Let the mention picker take Enter/Escape first.
                    if (mentionMatches.length > 0) {
                      if (e.key === 'Enter') { e.preventDefault(); applyMention(mentionMatches[0]); return; }
                      if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); return; }
                    }
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); }
                  }}
                  sx={{
                    flex: 1, px: 1.5, py: 0.9, fontSize: 13.5, bgcolor: CT.bone,
                    borderRadius: `${CT.radius.lg}px`, border: `1px solid ${CT.border}`,
                  }}
                />
                <IconButton
                  onClick={() => void handleSend()}
                  disabled={!draft.trim() || sending}
                  aria-label="Send message"
                  sx={{
                    width: 40, height: 40, bgcolor: CT.green, color: '#fff', transition: CT.motion,
                    '&:hover': { bgcolor: CT.greenHover },
                    '&.Mui-disabled': { bgcolor: CT.hairline, color: CT.faint },
                  }}
                >
                  {sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Composer emoji picker */}
      <EmojiPickerPopover
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        onPick={(emoji) => insertEmoji(emoji)}
      />

      {/* Conversation settings */}
      <Menu
        anchorEl={convMenuAnchor}
        open={Boolean(convMenuAnchor)}
        onClose={() => setConvMenuAnchor(null)}
        disableRestoreFocus
      >
        {activeConv?.is_group && (
          <MenuItem
            onClick={() => { setConvMenuAnchor(null); void openMembers(); }}
            sx={{ fontSize: 13.5, gap: 1 }}
          >
            <GroupIcon sx={{ fontSize: 17, color: CT.muted }} />
            View members
          </MenuItem>
        )}
        {activeConv?.is_group && isOwner && (
          <MenuItem
            onClick={() => {
              setRenameDraft(activeConv?.title || '');
              setRenaming(true);
              setConvMenuAnchor(null);
            }}
            sx={{ fontSize: 13.5 }}
          >
            Rename group
          </MenuItem>
        )}
        {activeConv?.is_group && (
          <MenuItem
            onClick={() => {
              setAddingPeople(true);
              setGroupMembers([]);
              setPeopleTerm('');
              void loadPeople('');
              setConvMenuAnchor(null);
            }}
            sx={{ fontSize: 13.5 }}
          >
            Add people
          </MenuItem>
        )}
        {activeConv?.is_group && (
          <MenuItem
            onClick={() => { setConvMenuAnchor(null); void handleLeave(); }}
            sx={{ fontSize: 13.5 }}
          >
            Leave group
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem
            onClick={() => { setConvMenuAnchor(null); setConfirmDelete(true); }}
            sx={{ fontSize: 13.5, color: '#B3261E' }}
          >
            Delete conversation
          </MenuItem>
        )}
        {!isOwner && !activeConv?.is_group && (
          <MenuItem disabled sx={{ fontSize: 12.5 }}>
            Only {activeConv?.others[0]?.username || 'the starter'} can delete this
          </MenuItem>
        )}
      </Menu>

      {/* Who's in this group */}
      <Dialog open={membersOpen} onClose={() => setMembersOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Members{members.length > 0 ? ` · ${members.length}` : ''}
        </DialogTitle>
        <DialogContent sx={{ px: 2 }}>
          {members.length === 0 ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}><CircularProgress size={20} /></Box>
          ) : members.map((person) => {
            const isSelf = person.id === selfId;
            const isCreator = activeConv?.created_by === person.id;
            return (
              <Box key={person.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1 }}>
                {personAvatar(person, 32)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: CT.ink }} noWrap>
                    {person.username}{isSelf ? ' (You)' : ''}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: CT.faint, fontFamily: CT.mono, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    {isCreator ? 'Group starter' : person.role || 'Member'}
                  </Typography>
                </Box>
                {/* The starter can remove anyone but themselves. */}
                {isOwner && !isSelf && (
                  <Button
                    size="small"
                    onClick={async () => {
                      if (!activeId) return;
                      try {
                        await removeMember(activeId, person.id);
                        setMembers((prev) => prev.filter((p) => p.id !== person.id));
                        await refreshList();
                      } catch (e: any) {
                        setError(e?.message || 'Unable to remove that person.');
                      }
                    }}
                    sx={{ textTransform: 'none', fontSize: 12, color: '#B3261E', minWidth: 0 }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Rename group */}
      <Dialog open={renaming} onClose={() => setRenaming(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Rename group</DialogTitle>
        <DialogContent>
          <InputBase
            autoFocus
            fullWidth
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void commitRename(); }}
            placeholder="Group name"
            sx={{
              mt: 1, px: 1.5, py: 1, fontSize: 14, bgcolor: CT.bone,
              borderRadius: `${CT.radius.md}px`, border: `1px solid ${CT.border}`,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void commitRename()}
            sx={{ textTransform: 'none', bgcolor: CT.green, '&:hover': { bgcolor: CT.greenHover } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add people to a group */}
      <Dialog open={addingPeople} onClose={() => setAddingPeople(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Add people</DialogTitle>
        <DialogContent sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.25, py: 0.75, bgcolor: CT.bone, borderRadius: `${CT.radius.md}px` }}>
            <SearchIcon sx={{ fontSize: 18, color: CT.muted }} />
            <InputBase
              autoFocus
              placeholder="Search people…"
              value={peopleTerm}
              onChange={(e) => { setPeopleTerm(e.target.value); void loadPeople(e.target.value); }}
              sx={{ flex: 1, fontSize: 13.5 }}
            />
          </Box>
          <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
            {people
              // Hide people already in the conversation.
              .filter((person) => !activeConv?.others.some((p) => p.id === person.id))
              .map((person) => {
                const picked = groupMembers.some((p) => p.id === person.id);
                return (
                  <Box
                    key={person.id}
                    onClick={() => toggleMember(person)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 1, cursor: 'pointer',
                      borderRadius: `${CT.radius.sm}px`,
                      bgcolor: picked ? CT.greenTint : 'transparent',
                      '&:hover': { bgcolor: picked ? CT.greenTint : CT.bone },
                    }}
                  >
                    {personAvatar(person, 30)}
                    <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: CT.ink }} noWrap>
                      {person.username}
                    </Typography>
                    {picked && <Typography sx={{ color: CT.green, fontWeight: 900 }}>✓</Typography>}
                  </Box>
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddingPeople(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={groupMembers.length === 0}
            onClick={() => void handleAddMembers(groupMembers)}
            sx={{ textTransform: 'none', bgcolor: CT.green, '&:hover': { bgcolor: CT.greenHover } }}
          >
            Add {groupMembers.length || ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete conversation — destructive, so it asks first. */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete this conversation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: CT.inkSoft }}>
            Every message{activeConv?.is_group ? ' in this group' : ''} will be permanently removed
            for everyone. This can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void handleDeleteConversation()}
            sx={{ textTransform: 'none', bgcolor: '#B3261E', '&:hover': { bgcolor: '#8C1D18' } }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit / delete for your own messages */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // Returning focus to the anchor blurred the edit box the instant it
        // opened, which is what stopped editing from working at all.
        disableRestoreFocus
      >
        {/* Quick reactions — available on anyone's message. */}
        <Box sx={{ display: 'flex', gap: 0.25, px: 1, pb: 0.5 }}>
          {QUICK_REACTIONS.map((emoji) => (
            <Box
              key={emoji}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (menuMessage) void handleReaction(menuMessage.id, emoji);
                setMenuAnchor(null);
              }}
              sx={{
                fontSize: 18, lineHeight: 1, p: 0.5, borderRadius: `${CT.radius.sm}px`,
                cursor: 'pointer', transition: CT.motion,
                '&:hover': { bgcolor: CT.greenTint, transform: 'scale(1.15)' },
              }}
            >
              {emoji}
            </Box>
          ))}
        </Box>
        {menuMessage?.sender_id === selfId && <Divider sx={{ my: 0.5 }} />}
        {menuMessage?.sender_id === selfId && (
          <MenuItem
            disabled={Boolean(menuMessage?.attachment_path)}
            onClick={() => {
              if (menuMessage) { setEditingId(menuMessage.id); setEditDraft(menuMessage.body); }
              setMenuAnchor(null);
            }}
            sx={{ fontSize: 13.5 }}
          >
            Edit
          </MenuItem>
        )}
        {menuMessage?.sender_id === selfId && (
          <MenuItem
            onClick={() => {
              if (menuMessage) void removeMessage(menuMessage);
              setMenuAnchor(null);
            }}
            sx={{ fontSize: 13.5, color: '#B3261E' }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ChatWidget;
